import client from './client';

/**
 * API Survey - Gestion des sondages
 * Les erreurs sont gérées centralement par le client Axios et les intercepteurs
 * Utiliser useApiCall() dans les composants pour la gestion des erreurs
 */

// ============ SURVEYS API ============

/**
 * Récupère la liste des sondages
 * @param {Object} params - Paramètres de pagination/filtrage
 * @returns {Promise} Liste des sondages
 */
export const fetchSurveys = async (params = {}) => {
  const response = await client.get('/api/survey/', { params });
  return response.data;
};

/**
 * Récupère les sondages de l'utilisateur connecté (gérant)
 * Appelle l'endpoint /api/survey/manager/surveys/ qui filtre par creator=request.user
 * @param {Object} params - Paramètres de pagination/filtrage
 * @returns {Promise} Liste des sondages de l'utilisateur
 */
export const fetchMySurveys = async (params = {}) => {
  const response = await client.get('/api/survey/manager/surveys/', { params });
  return response.data;
};

/**
 * Récupère les sondages des gérants pour l'admin
 * Appelle l'endpoint /api/surveys/admin/surveys/ qui retourne tous les sondages des gérants
 * @param {Object} params - Paramètres de pagination/filtrage (title, creator_id)
 * @returns {Promise} Liste des sondages des gérants
 */
export const fetchAdminSurveys = async (params = {}) => {
  const response = await client.get('/api/survey/admin/surveys/', { params });
  return response.data;
};

/**
 * Récupère un sondage par son ID
 * @param {number|string} id - ID du sondage
 * @returns {Promise} Données du sondage
 */
export const fetchSurveyById = async (id) => {
  const response = await client.get(`/api/survey/${id}/`);
  return response.data?.data || response.data;
};

/**
 * Crée un nouveau sondage
 * @param {Object} surveyData - Données du sondage
 * @returns {Promise} Sondage créé
 */
export const createSurvey = async (surveyData) => {
  const response = await client.post('/api/survey/create/', surveyData);
  return response.data;
};

/**
 * Met à jour un sondage
 * @param {number|string} id - ID du sondage
 * @param {Object} surveyData - Données à mettre à jour
 * @returns {Promise} Sondage mis à jour
 */
export const updateSurvey = async (id, surveyData) => {
  const response = await client.put(`/api/survey/${id}/update/`, surveyData);
  return response.data;
};

/**
 * Supprime un sondage
 * @param {number|string} id - ID du sondage
 * @returns {Promise} Réponse de suppression
 */
export const deleteSurvey = async (id) => {
  const response = await client.delete(`/api/survey/${id}/delete/`);
  return response.data;
};

/**
 * Archive ou désarchive un sondage
 * @param {number|string} id - ID du sondage
 * @param {boolean} archived - État d'archivage
 * @returns {Promise} Réponse d'archivage
 */
export const archiveSurvey = async (id, archived = true) => {
  const response = await client.post(`/api/survey/manager/surveys/${id}/archive/`, { archived });
  return response.data;
};

/**
 * Soumet les réponses à un sondage
 * @param {number|string} surveyId - ID du sondage
 * @param {Object} responses - Réponses aux questions
 * @returns {Promise} Confirmation de soumission
 */
export const submitSurveyResponse = async (surveyId, responses) => {
  const response = await client.post(`/api/survey/${surveyId}/responses/`, responses);
  return response.data;
};

/**
 * Récupère les résultats d'un sondage
 * @param {number|string} surveyId - ID du sondage
 * @returns {Promise} Résultats du sondage
 */
/**
 * Récupère les résultats d'un sondage
 * @param {number|string} surveyId - ID du sondage
 * @returns {Promise} Résultats du sondage avec statistiques et réponses
 */
export const fetchSurveyResults = async (surveyId) => {
  const response = await client.get(`/api/survey/${surveyId}/responses/`);
  return response.data;
};

/**
 * Télécharge le rapport d'un sondage
 * @param {number|string} surveyId - ID du sondage
 * @param {string} format - Format du rapport (pdf, excel)
 * @returns {Promise} { blob, filename }
 */
export const downloadSurveyReport = async (surveyId, format = 'pdf') => {
  const response = await client.get(`/api/survey/${surveyId}/report/`, {
    params: { format },
    responseType: 'blob',
  });

  const contentDisposition = response.headers['content-disposition'] || '';
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  const filename = match ? match[1] : `survey_${surveyId}_report.${format === 'excel' ? 'xlsx' : 'pdf'}`;

  return { blob: response.data, filename };
};

// ============ QUESTIONS API ============

/**
 * Récupère les questions d'un sondage
 * @param {number|string} surveyId - ID du sondage
 * @returns {Promise} Liste des questions
 */
export const fetchQuestions = async (surveyId) => {
  const response = await client.get(`/api/survey/${surveyId}/questions/`);
  return response.data;
};

/**
 * Crée une nouvelle question
 * @param {number|string} surveyId - ID du sondage
 * @param {Object} questionData - Données de la question
 * @returns {Promise} Question créée
 */
export const createQuestion = async (surveyId, questionData) => {
  const response = await client.post(`/api/survey/${surveyId}/questions/`, questionData);
  return response.data;
};

/**
 * Met à jour une question
 * @param {number|string} surveyId - ID du sondage
 * @param {number|string} questionId - ID de la question
 * @param {Object} questionData - Données à mettre à jour
 * @returns {Promise} Question mise à jour
 */
export const updateQuestion = async (surveyId, questionId, questionData) => {
  const response = await client.put(`/api/survey/${surveyId}/questions/${questionId}/`, questionData);
  return response.data;
};

/**
 * Supprime une question
 * @param {number|string} surveyId - ID du sondage
 * @param {number|string} questionId - ID de la question
 * @returns {Promise} Réponse de suppression
 */
export const deleteQuestion = async (surveyId, questionId) => {
  const response = await client.delete(`/api/survey/${surveyId}/questions/${questionId}/`);
  return response.data;
};

// ============ MODELS/TEMPLATES API ============

/**
 * Récupère la liste des modèles de sondage
 * @param {Object} params - Paramètres de pagination/filtrage
 * @returns {Promise} Liste des modèles
 */
export const fetchModels = async (params = {}) => {
  const response = await client.get('/api/survey/models/', { params });
  return response.data;
};

/**
 * Récupère un modèle par son ID
 * @param {number|string} id - ID du modèle
 * @returns {Promise} Données du modèle
 */
export const fetchModelById = async (id) => {
  const response = await client.get(`/api/survey/models/${id}/`);
  return response.data?.data || response.data;
};

/**
 * Crée un nouveau modèle
 * @param {Object} modelData - Données du modèle
 * @returns {Promise} Modèle créé
 */
export const createModel = async (modelData) => {
  const response = await client.post('/api/survey/models/create/', modelData);
  return response.data;
};

/**
 * Met à jour un modèle
 * @param {number|string} id - ID du modèle
 * @param {Object} modelData - Données à mettre à jour
 * @returns {Promise} Modèle mis à jour
 */
export const updateModel = async (id, modelData) => {
  const response = await client.put(`/api/survey/models/${id}/update/`, modelData);
  return response.data;
};

/**
 * Supprime un modèle
 * @param {number|string} id - ID du modèle
 * @returns {Promise} Réponse de suppression
 */
export const deleteModel = async (id) => {
  const response = await client.delete(`/api/survey/models/${id}/delete/`);
  return response.data;
};

/**
 * Utilise un modèle pour créer un sondage
 * @param {number|string} modelId - ID du modèle
 * @param {Object} surveyData - Données du sondage
 * @returns {Promise} Sondage créé à partir du modèle
 */
export const useModel = async (modelId, surveyData) => {
  const response = await client.post(`/api/survey/models/${modelId}/use/`, surveyData);
  return response.data;
};

/**
 * Récupère les statistiques des participants pour un sondage
 * @param {number|string} surveyId - ID du sondage
 * @returns {Promise} Statistiques des participants
 */
export const fetchParticipantStats = async (surveyId) => {
  const response = await client.get(`/api/survey/${surveyId}/participant-stats/`);
  return response.data;
};
